import { Reveal, Slug } from "@/components/minka/Motion";
import { Seo } from "@/components/minka/Seo";

const CHARS = [
  { img: "/img/chars/rika.jpg", name: "RIKA", role: "The Heroine", desc: "34. Japanese-American. Hip and modern — choppy shoulder-length hair with magenta stripes, a diamond in her nose, cowboy boots under an indigo Japanese apron." },
  { img: "/img/chars/carpenter.jpg", name: "THE CARPENTER", role: "Love Interest", desc: "A local Japanese carpenter. Square-jawed and handsome with slanted eyes, muscular, six feet tall, shoulder-length hair — dressed in traditional blue indigo carpenter apparel." },
  { img: "/img/chars/monk.jpg", name: "THE MONK", role: "Buddhist Priest", desc: "In his mid-50s. Bald head, traditional Buddhist apparel — the uncle who attempts the failed spiritual intervention that reveals the portal to Hell." },
  { img: "/img/chars/biker.jpg", name: "THE SATANIC BIKER", role: "Antagonist", desc: "A Bosozoku biker in denim covered with patches — and a very bad attitude. A crude, unpredictable human threat in the valley." },
  { img: "/img/chars/ghost.jpg", name: "THE GHOST", role: "The Samurai Spirit", desc: "A 1500s samurai woman in a dirty white funeral kimono, waist-length hair cascading around a beautiful aristocratic narrow face — bound to the house by betrayal." },
  { img: "/img/chars/kid.jpg", name: "THE KID", role: "The Samurai Child", desc: "A 3-year-old samurai child in a dirt-white funeral kimono. Innocence caught in a centuries-old curse." },
];

const SLIDES = [
  { n: 1, label: "Cover" },
  { n: 2, label: "The Story" },
  { n: 3, label: "The Setting" },
  { n: 4, label: "The Characters" },
  { n: 5, label: "Visual Style" },
  { n: 6, label: "Audience" },
  { n: 7, label: "Production" },
  { n: 8, label: "Rights & Contact" },
];

const Char = ({ c }) => (
  <Reveal className="group">
    <div className="relative aspect-[3/4] overflow-hidden border border-white/10">
      <img src={c.img} alt={`${c.name} — ${c.role}`} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" data-testid={`pitch-char-${c.name.toLowerCase().replace(/ /g, "-")}`} />
      <div className="absolute inset-0 bg-gradient-to-t from-[#07080B] via-transparent to-transparent" />
      <div className="absolute bottom-0 p-5">
        <p className="font-display text-2xl font-bold tracking-[0.1em] text-[#E2E0D8]">{c.name}</p>
        <p className="font-mono-s text-[10px] uppercase tracking-[0.25em] text-[#E76F51]">{c.role}</p>
      </div>
    </div>
    <p className="mt-3 text-sm leading-relaxed text-[#A3ACBB]">{c.desc}</p>
  </Reveal>
);

const Slide = ({ n, label, children, dark = false }) => (
  <section className={`relative flex min-h-[92svh] flex-col justify-center px-5 py-20 md:px-14 ${dark ? "bg-[#07080B]/90" : "bg-[#07080B]/70"} backdrop-blur-[2px]`} data-testid={`pitch-slide-${n}`}>
    <div className="mx-auto w-full max-w-[1300px]">
      <p className="font-mono-s text-xs tracking-[0.3em] text-[#A8DADC]">{String(n).padStart(2, "0")} / {SLIDES.length} — {label}</p>
      {children}
    </div>
  </section>
);

export default function PitchDeck() {
  return (
    <div data-testid="pitch-deck-page">
      <Seo
        title="MINKA Pitch Deck — Japanese Supernatural Horror"
        description="Producer pitch deck for MINKA: story, Iwate setting, characters, visual style, audience and production possibilities. By Nicola Davis."
        path="/pitch-deck"
      />
      <Slide n={1} label="Cover">
        <Reveal>
          <p className="font-mono-s text-xs uppercase tracking-[0.35em] text-[#A8DADC]">Producer pitch deck · 企画書</p>
          <h1 className="mt-6 font-brush text-[26vw] leading-none text-[#C81D25] ink-damage sm:text-[16vw]" data-testid="pitch-title">MINKA</h1>
          <p className="mt-4 font-serif-i text-2xl text-[#E2E0D8] md:text-4xl">The past refuses to die. The road to redemption leads through Hell.</p>
          <p className="mt-6 font-mono-s text-xs uppercase tracking-[0.25em] text-[#A3ACBB]">A feature screenplay by Nicola Davis · Supernatural Thriller / Horror · Available for option & purchase</p>
        </Reveal>
      </Slide>

      <Slide n={2} label="The Story" dark>
        <Reveal>
          <h2 className="font-display text-3xl font-bold text-[#E2E0D8] sm:text-5xl">One house. One curse. <span className="font-serif-i font-normal text-[#E76F51]">One descent into Hell.</span></h2>
          <div className="mt-8 grid gap-8 md:grid-cols-3">
            {[["I · 相続", "Rika, a Japanese-American artist, inherits her late mother's secret minka deep in Iwate's “Devil's Hand” mountains — and finds it does not want her. A samurai ghost stalks the halls, Satanic bikers menace the valley, and something older still waits below."],
              ["II · 呪い", "A carpenter's kindness, a priest's failed ritual, and a doorway hidden behind the Shinto shrine: a portal to Hell and a curse born of a 1500s betrayal. Rika chooses to fight — training in budō, shaving her head in renunciation of fear."],
              ["III · 地獄", "She descends the underworld, survives a flaming demon, frees the samurai spirit from purgatory, and breaks the cycle of vengeance — returning to belonging, purpose, and love."]].map(([k, v]) => (
              <div key={k} className="border-l-2 border-[#C81D25] pl-5">
                <p className="font-display text-xl text-[#C81D25]">{k}</p>
                <p className="mt-3 text-sm leading-relaxed text-[#C3CAD5] md:text-base">{v}</p>
              </div>
            ))}
          </div>
        </Reveal>
      </Slide>

      <Slide n={3} label="The Setting">
        <Reveal>
          <h2 className="font-display text-3xl font-bold text-[#E2E0D8] sm:text-5xl">Iwate Prefecture — <span className="font-serif-i font-normal text-[#E76F51]">lost in time.</span></h2>
          <p className="mt-6 max-w-3xl text-base leading-relaxed text-[#C3CAD5] md:text-lg">A remote village in the mountains of northern Iwate Prefecture, where centuries-old magariya farmhouses still stand and old beliefs never left. Fog pooled in the valleys, lantern-lit farmhouses, shrines older than the maps — a world modern life seems to have forgotten.</p>
        </Reveal>
        <div className="mt-10 grid gap-6 md:grid-cols-2">
          <Reveal><img src="/img/hero.jpg" alt="The minka and torii in the Iwate mountains" className="aspect-[3/2] w-full border border-white/10 object-cover" data-testid="pitch-setting-image" /><p className="mt-3 font-mono-s text-xs uppercase tracking-[0.2em] text-[#A3ACBB]">The minka & the gate — the family's inheritance</p></Reveal>
          <Reveal delay={0.1}><img src="/img/interior.jpg" alt="The haunted farmhouse corridor" className="aspect-[3/2] w-full border border-white/10 object-cover" data-testid="pitch-visual-image" /><p className="mt-3 font-mono-s text-xs uppercase tracking-[0.2em] text-[#A3ACBB]">Inside — where the past waits</p></Reveal>
        </div>
      </Slide>

      <Slide n={4} label="The Characters" dark>
        <Reveal>
          <h2 className="font-display text-3xl font-bold text-[#E2E0D8] sm:text-5xl">The Characters <span className="font-serif-i font-normal text-[#E76F51]">· 登場人物</span></h2>
        </Reveal>
        <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {CHARS.map((c) => <Char key={c.name} c={c} />)}
        </div>
      </Slide>

      <Slide n={5} label="Visual Style">
        <Reveal>
          <h2 className="font-display text-3xl font-bold text-[#E2E0D8] sm:text-5xl">Visual style — <span className="font-serif-i font-normal text-[#E76F51]">beauty hiding dread.</span></h2>
          <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {[["Moonlit noir", "Cold blue-black nights, one red signal in every frame"], ["Folk texture", "Paper shoji, soot-dark beams, ritual objects of a living past"], ["The feminine ghost tradition", "Stillness, hair, white kimono — dread before violence"], ["Heat and hell", "Molten undercolors for the underworld's final act"]].map(([k, v]) => (
              <div key={k} className="border border-white/10 bg-[#0E1017]/85 p-6" data-testid={`pitch-style-${k.split(" ")[0].toLowerCase()}`}>
                <p className="font-display text-xl text-[#E2E0D8]">{k}</p>
                <p className="mt-3 text-sm leading-relaxed text-[#A3ACBB]">{v}</p>
              </div>
            ))}
          </div>
        </Reveal>
      </Slide>

      <Slide n={6} label="Audience">
        <Reveal>
          <h2 className="font-display text-3xl font-bold text-[#E2E0D8] sm:text-5xl">Target audience</h2>
          <p className="mt-6 max-w-3xl text-base leading-relaxed text-[#C3CAD5] md:text-lg">Think a Japanese version of <strong className="text-[#E2E0D8]">Twilight</strong>, meets <strong className="text-[#E2E0D8]">Ring</strong>, meets <strong className="text-[#E2E0D8]">Rocky</strong>. MINKA bridges horror audiences and viewers who enjoy emotionally driven supernatural stories: the young adult crossover crowd that made Twilight a phenomenon, J-horror fans raised on The Ring, and martial-arts cinephiles. Female-forward hero story, authentic Japanese setting, franchise-friendly mythology.</p>
        </Reveal>
      </Slide>

      <Slide n={7} label="Production">
        <Reveal>
          <h2 className="font-display text-3xl font-bold text-[#E2E0D8] sm:text-5xl">Production possibilities</h2>
          <div className="mt-8 space-y-px border border-white/10 bg-white/10">
            {[["01", "New Mexico", "Interior scenes — Santa Fe Studio · Exterior scenes — Ruidoso or Red River"],
              ["02", "Canada", "Proven incentive-friendly stand-in for rural Japan"],
              ["03", "Japan", "Morioka city and / or countryside — the real Iwate"]].map(([n, p, d]) => (
              <div key={n} className="grid grid-cols-[50px_1fr] gap-4 bg-[#0E1017]/95 px-4 py-6 md:grid-cols-[60px_240px_1fr] md:px-6" data-testid={`pitch-production-${n}`}>
                <span className="font-mono-s text-xs text-[#A8DADC]">{n}</span>
                <span className="font-display text-2xl text-[#E2E0D8]">{p}</span>
                <span className="col-span-2 pl-[50px] text-sm text-[#A3ACBB] md:col-span-1 md:pl-0 md:text-base">{d}</span>
              </div>
            ))}
          </div>
          <p className="mt-6 text-sm text-[#A3ACBB]">Contained cast, single primary location, practical folklore effects with a contained VFX footprint — a prestige horror package at a controlled budget.</p>
        </Reveal>
      </Slide>

      <Slide n={8} label="Rights & Contact" dark>
        <Reveal>
          <h2 className="font-display text-3xl font-bold text-[#E2E0D8] sm:text-5xl">Available now.</h2>
          <p className="mt-4 max-w-2xl text-base leading-relaxed text-[#C3CAD5] md:text-lg">Option agreement, outright purchase, or shopping agreement. Every inquiry goes directly to the writer.</p>
          <div className="mt-8 flex flex-col gap-3 font-mono-s text-sm">
            <a href="mailto:nini.laughlin@gmail.com" className="link-underline w-fit break-all text-[#E2E0D8]" data-testid="pitch-email-link">nini.laughlin@gmail.com</a>
            <a href="tel:5056600596" className="link-underline w-fit text-[#E2E0D8]" data-testid="pitch-phone-link">505-660-0596</a>
          </div>
          <a href="/film-options" className="mt-10 inline-block rounded-full bg-[#C81D25] px-8 py-4 font-mono-s text-xs uppercase tracking-[0.25em] text-[#E2E0D8] transition-colors duration-300 hover:bg-[#E76F51]" data-testid="pitch-options-cta">Send an inquiry</a>
        </Reveal>
      </Slide>
    </div>
  );
}
